import React, { useState } from 'react';
import './scss/SubscriptionManagement.scss';
import { Info, ExpandMore, ExpandLess } from '../icons';
import { getPlanPrice } from '../../constants/pricing';

const SubscriptionManagement = ({ billingInfo, shortId, onUpdate }) => {
    const [showStackDetails, setShowStackDetails] = useState(false);

    if (!billingInfo || billingInfo.planType === 'Free') {
        return null;
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isAppSumo = billingInfo.planType === 'AppSumo';
    const isDealMirror = billingInfo.planType === 'DealMirror';

    const cumulativeBenefits = billingInfo.cumulativeBenefits || null;
    const redeemedCodes = cumulativeBenefits?.redeemedCodes || { appSumo: [], dealMirror: [] };
    const appSumoCodes = redeemedCodes.appSumo || [];
    const dealMirrorHistory = redeemedCodes.dealMirror || [];

    // Check if ACTUALLY trialing (backend now returns isTrialing only when trial is active and not expired)
    const isTrialing = billingInfo?.trial?.isTrialing === true;
    const daysRemaining = billingInfo?.trial?.daysRemaining || 0;

    // Trial expired: trial was used but not currently active
    const hasExpiredTrial = billingInfo?.trial?.trialUsed && !isTrialing && billingInfo.planType === 'Free';

    return (
        <div className="subscription-management">
            <div className="subscription-header">
                <div className="plan-title-row">
                    <h3>{billingInfo.planType === 'DealMirror' ? 'Lifetime' : billingInfo.planType}</h3>
                    <span className={`status-pill ${isTrialing ? 'trial' : hasExpiredTrial ? 'expired' : billingInfo?.billing?.cancelAtPeriodEnd ? 'ending' : 'active'}`}>
                        {isTrialing ? 'Trial' : hasExpiredTrial ? 'Trial Expired' : billingInfo?.billing?.cancelAtPeriodEnd ? 'Ending' : 'Active'}
                    </span>
                </div>

                {isTrialing && (
                    <div className="notice trial">
                        <Info size={16} />
                        <span>Trial ends {formatDate(billingInfo?.trial?.trialEndDate)} · {Math.max(0, daysRemaining)} days left</span>
                    </div>
                )}

                {hasExpiredTrial && (
                    <div className="notice warning">
                        <Info size={16} />
                        <span>Trial ended {formatDate(billingInfo?.trial?.trialEndDate)} · Upgrade to continue using Premium features</span>
                    </div>
                )}

                {!isTrialing && !hasExpiredTrial && billingInfo.billing.cancelAtPeriodEnd && (
                    <div className="notice warning">
                        <Info size={16} />
                        <span>Ends {formatDate(billingInfo?.billing?.currentPeriodEnd || billingInfo?.billing?.nextBillingDate)} · Switches to Free</span>
                    </div>
                )}
            </div>

            <div className="subscription-details">
                {isTrialing ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Active Seats</span>
                            <span className="value">{billingInfo.activeMembers || 0}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Trial Period</span>
                            <span className="value">{formatDate(billingInfo?.trial?.trialStartDate)} → {formatDate(billingInfo?.trial?.trialEndDate)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Days Remaining</span>
                            <span className="value">{Math.max(0, daysRemaining)} days</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">After Trial</span>
                            <span className="value muted">Reverts to Free plan</span>
                        </div>
                    </>
                ) : hasExpiredTrial ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Trial Period</span>
                            <span className="value">{formatDate(billingInfo?.trial?.trialStartDate)} → {formatDate(billingInfo?.trial?.trialEndDate)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Trial Status</span>
                            <span className="value" style={{ color: '#f87171' }}>Expired</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Current Plan</span>
                            <span className="value muted">Free plan (Limited features)</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">To Continue</span>
                            <span className="value" style={{ color: 'var(--brand-color)' }}>Upgrade to {billingInfo.planType} plan</span>
                        </div>
                    </>
                ) : isAppSumo && billingInfo.appSumo ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Tier</span>
                            <span className="value">
                                {billingInfo.appSumo.tier.replace('tier', 'Tier ')}
                                {appSumoCodes.length > 1 && (
                                    <span
                                        className="badge stack-toggle"
                                        onClick={() => setShowStackDetails((prev) => !prev)}
                                    >
                                        {appSumoCodes.length} codes stacked
                                    </span>
                                )}
                            </span>
                        </div>
                        {showStackDetails && appSumoCodes.length > 1 && (
                            <div className="stacked-details">
                                {appSumoCodes.map((code, index) => (
                                    <div className="stack-chip" key={code.code || index}>
                                        <span className="stack-title">
                                            {code.tier ? code.tier.replace('tier', 'Tier ') : 'Code ' + (index + 1)}
                                        </span>
                                        <span className="stack-meta">
                                            {code.seats ? code.seats + ' seats' : null}
                                            {code.seats && code.storage ? ' / ' : ''}
                                            {code.storage ? code.storage + 'GB' : null}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="detail-row">
                            <span className="label">Seats</span>
                            <span className="value">
                                {billingInfo.activeMembers || 0} / {(cumulativeBenefits?.totalSeats || billingInfo.appSumo.seats)}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Storage</span>
                            <span className="value">{(cumulativeBenefits?.totalStorage || billingInfo.appSumo.storage)}GB</span>
                        </div>
                        {cumulativeBenefits?.hasWhiteLabel && (
                            <div className="detail-row">
                                <span className="label">White Label</span>
                                <span className="value"><span className="value-pill is-yes">Included</span></span>
                            </div>
                        )}
                        <div className="detail-row">
                            <span className="label">Purchase Date</span>
                            <span className="value">{formatDate(billingInfo.appSumo.purchaseDate)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Billing</span>
                            <span className="value">Lifetime Deal <span className="badge">No fees</span></span>
                        </div>

                    </>
                ) : isDealMirror && billingInfo.dealMirror ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Plan</span>
                            <span className="value">Lifetime Deal</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Tier</span>
                            <span className="value">
                                {billingInfo.dealMirror.tier?.replace('tier', 'Tier ')}
                                {cumulativeBenefits?.stacks > 1 && (
                                    <span
                                        className="badge stack-toggle"
                                        onClick={() => setShowStackDetails((prev) => !prev)}
                                    >
                                        {cumulativeBenefits.stacks} tiers stacked
                                        {showStackDetails ? <ExpandLess size={14} /> : <ExpandMore size={14} />}
                                    </span>
                                )}
                            </span>
                        </div>
                        {showStackDetails && dealMirrorHistory.length > 1 && (
                            <div className="stack-history">
                                {dealMirrorHistory.map((entry, index) => (
                                    <div className="stack-history-row" key={index}>
                                        <span className="shr-left">
                                            <span className="shr-tier">{entry.tier ? entry.tier.replace('tier', 'Tier ') : 'Purchase ' + (index + 1)}</span>
                                            <span className="shr-sub">{entry.source === 'redemption' ? 'Redeemed' : 'Purchased'}{entry.redeemedAt ? ` · ${formatDate(entry.redeemedAt)}` : ''}</span>
                                        </span>
                                        <span className="shr-meta">
                                            {entry.seats ? `+${entry.seats} seats` : null}
                                            {entry.seats && entry.storage ? ' · ' : ''}
                                            {entry.storage ? `+${entry.storage}GB` : null}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="detail-row">
                            <span className="label">Seats</span>
                            <span className="value">
                                {billingInfo.activeMembers || 0} / {cumulativeBenefits?.totalSeats || billingInfo.dealMirror.seats}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Storage</span>
                            <span className="value">{cumulativeBenefits?.totalStorage || billingInfo.dealMirror.storage}GB</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">White Label</span>
                            <span className="value">
                                {(cumulativeBenefits?.hasWhiteLabel ?? billingInfo.dealMirror.whiteLabel)
                                    ? <span className="value-pill is-yes">Included</span>
                                    : <span className="value-pill is-no">Not included</span>}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">{billingInfo.dealMirror.source === 'purchase' ? 'Purchased' : 'Redeemed'}</span>
                            <span className="value">{formatDate(billingInfo.dealMirror.redeemedAt)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">License Type</span>
                            <span className="value">Lifetime License (One-time payment, no recurring charges)</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="detail-row">
                            <span className="label">Seats</span>
                            <span className="value">
                                {billingInfo.activeMembers || 0} / {billingInfo.allocatedSeats || 0}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Price per Seat</span>
                            <span className="value">
                                ${getPlanPrice(billingInfo.planType, billingInfo.billingCycle)}/{billingInfo.billingCycle === 'annually' ? 'yr' : 'mo'}
                                {billingInfo.billingCycle === 'annually' && (
                                    <span className="badge">-17%</span>
                                )}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Total Cost</span>
                            <span className="value">
                                ${(getPlanPrice(billingInfo.planType, billingInfo.billingCycle) * (billingInfo.allocatedSeats || 0)).toFixed(2)}/{billingInfo.billingCycle === 'annually' ? 'yr' : 'mo'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Billing Cycle</span>
                            <span className="value">{billingInfo.billingCycle === 'annually' ? 'Annual' : 'Monthly'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Next Billing</span>
                            <span className="value">{formatDate(billingInfo?.billing?.nextBillingDate || billingInfo?.billing?.currentPeriodEnd)}</span>
                        </div>
                        {/* {billingInfo?.createdAt && (
                            <div className="detail-row">
                                <span className="label">Member Since</span>
                                <span className="value">{formatDate(billingInfo?.createdAt)}</span>
                            </div>
                        )} */}
                    </>
                )}
            </div>

        </div>
    );
};

export default SubscriptionManagement;
